import { CURRENT_TOS_VERSION } from '@shared/constants/tos-version.constant';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useTranslation } from 'react-i18next';
import { ConsentService } from '../../services/consent/consent.service';
import type {
  IConsentService,
  UpdateConsentRequest,
} from '../../services/consent/consent.service.interface';
import { useAnalytics } from '../analytics/use-analytics';
import { useValidatedTenantId } from '../auth/use-validated-tenant-id';
import { useNotification } from '../use-notification';

const EPOCH_ZERO = '1970-01-01T00:00:00.000Z';

const CONSENT_ROOT = ['consent'] as const;

export const consentQueryKeys = {
  root: () => CONSENT_ROOT,
  byTenant: (tenantId: string | null | undefined) => [...CONSENT_ROOT, tenantId] as const,
};

export function useConsent() {
  const service = useInjection<IConsentService>(ConsentService);
  const queryClient = useQueryClient();
  const { showError } = useNotification();
  const { t } = useTranslation();
  const currentTenantId = useValidatedTenantId();
  const { trackEvent } = useAnalytics();

  const {
    data: consents,
    isLoading,
    isPending: isConsentsPending,
    isError,
  } = useQuery({
    queryKey: consentQueryKeys.byTenant(currentTenantId),
    queryFn: () => service.getUserConsents(),
    retry: 1,
    enabled: !!currentTenantId,
  });

  const {
    mutate: updateConsent,
    mutateAsync: updateConsentAsync,
    isPending,
  } = useMutation({
    mutationFn: (data: UpdateConsentRequest) => service.updateConsent(data),
    onSuccess: (newConsents, variables) => {
      queryClient.setQueryData(consentQueryKeys.byTenant(currentTenantId), newConsents);
      trackEvent('consent_update', {
        consent_type: variables.consentType,
        granted: variables.granted,
      });
    },
    onError: (err) => {
      console.error('useConsent updateConsent error:', err);
      showError(t('common.error'));
    },
  });

  const getConsent = (consentType: string) =>
    consents?.find((c) => c.consentType === consentType)?.granted ?? false;

  // MARKETING é incluso: chi non l'ha mai visto deve dare (o negare) il
  // consenso ads prima che i pixel possano caricarsi.
  const GdprConsentTypes = ['PLATFORM_IMPROVEMENT', 'ANONYMIZED_STATISTICS', 'MARKETING'];

  const needsConsentPrompt =
    !isLoading &&
    consents != null &&
    GdprConsentTypes.some((type) => {
      const consent = consents.find((c) => c.consentType === type);
      return !consent || consent.updatedAt === EPOCH_ZERO;
    });

  const needsTosAcceptance =
    !isLoading &&
    consents != null &&
    (() => {
      const tosAcceptance = consents.find((c) => c.consentType === 'TOS_ACCEPTANCE');
      const unfairTerms = consents.find((c) => c.consentType === 'TOS_UNFAIR_TERMS_1341');
      return (
        !tosAcceptance?.granted ||
        !unfairTerms?.granted ||
        tosAcceptance.clauseVersion !== CURRENT_TOS_VERSION ||
        unfairTerms.clauseVersion !== CURRENT_TOS_VERSION
      );
    })();

  return {
    consents,
    isLoading,
    isConsentsPending,
    isError,
    updateConsent,
    updateConsentAsync,
    isPending,
    getConsent,
    needsConsentPrompt,
    needsTosAcceptance,
    CURRENT_TOS_VERSION,
  };
}
